import React, { useRef, useState } from 'react';
import { Configuration } from '@spotify-internal/remote-config-properties';
import { RemoteConfigContext } from './remoteConfigContext';
import { isObject, isPromiseLike, } from '@spotify-internal/remote-config-common';
export class SuspendedConfiguration extends Configuration.Delegate {
    constructor() {
        super(() => {
            if (!this.delegate)
                throw this.suspendedPromise;
            return this.delegate;
        });
        this.suspendedPromise = new Promise(res => {
            this.resolveSuspense = configuration => {
                if (this.delegate)
                    throw new Error('Already resolved');
                this.delegate = configuration;
                res();
            };
        });
    }
    get isSuspended() {
        return !this.delegate;
    }
    static isSuspended(config) {
        return config instanceof SuspendedConfiguration && config.isSuspended;
    }
}
export const RemoteConfigProvider = ({ children, configuration }) => {
    const [currentConfig, updateConfig] = useState(new SuspendedConfiguration());
    useResolved(configuration, configuration => {
        if (SuspendedConfiguration.isSuspended(currentConfig)) {
            currentConfig.resolveSuspense(configuration);
        }
        updateConfig(configuration);
    });
    return (React.createElement(RemoteConfigContext.Provider, { value: currentConfig }, children));
};
export const ManagedRemoteConfigProvider = ({ children, resolver: ResolverClass, options, context, initialConfiguration, }) => {
    const resolverInstance = useComputation((prevResolver) => {
        if (prevResolver) {
            prevResolver.destroy();
        }
        return new ResolverClass(Object.assign({ initialConfiguration }, options));
    }, [ResolverClass, options]);
    const [configuration, updateConfiguration] = useState(initialConfiguration || null);
    useComputation(() => {
        if (context) {
            resolverInstance.resolve(context).then(updateConfiguration);
        }
    }, [resolverInstance, context]);
    return (React.createElement(RemoteConfigProvider, { configuration: configuration }, children));
};
function useResolved(maybePromise, onValue) {
    const lastMaybePromise = useRef(null);
    if (!maybePromise || lastMaybePromise.current === maybePromise) {
        return;
    }
    lastMaybePromise.current = maybePromise;
    if (!isPromiseLike(maybePromise)) {
        onValue(maybePromise);
        return;
    }
    maybePromise.then(onValue);
}
function useComputation(factory, dependencies) {
    const prevDeps = useRef();
    const prevValue = useRef();
    if (!equalToDepth(dependencies, prevDeps.current, 2)) {
        prevDeps.current = [...dependencies];
        prevValue.current = factory(prevValue.current);
    }
    return prevValue.current;
}
function equalToDepth(a, b, depth = 0) {
    if (a === b)
        return true;
    if (depth === 0)
        return false;
    if (!isObject(a) || !isObject(b))
        return false;
    const keys = Object.keys(a);
    if (keys.length !== Object.keys(b).length)
        return false;
    for (const key of keys) {
        if (!equalToDepth(a[key], b[key], depth - 1))
            return false;
    }
    return true;
}
//# sourceMappingURL=remoteConfigProvider.js.map